import { useQuery } from '@tanstack/react-query';

import { useCapability } from '../../hooks/use-capability';
import { api } from '../../lib/api-client';

import type { ConductProfileResolution } from './conduct-types';
import type { UseQueryResult } from '@tanstack/react-query';

/**
 * Fase 2 «Conduta e Contexto» — leitura da conduta resolvida.
 *
 * Gate `ai-conduct-profile-read` threaded no `enabled` da query, não só no render: sem a
 * cap, a tela nem dispara o fetch (memória `feedback_gatear_fetch_nao_so_render` — gatear só
 * o JSX deixa o request sair e voltar 403 no console).
 */
export const CONDUCT_PROFILE_QUERY_KEY = 'conduct-profile-effective';
export const CONDUCT_PERSONA_USAGE_QUERY_KEY = 'conduct-persona-usage';

/** A cap de leitura das 3 telas de conduta. */
export const CONDUCT_PROFILE_CAPABILITY = 'ai-conduct-profile-read';

export function useEffectiveConductProfile(
  playbookId?: string,
): UseQueryResult<ConductProfileResolution> {
  const canRead = useCapability(CONDUCT_PROFILE_CAPABILITY);
  return useQuery({
    queryKey: [CONDUCT_PROFILE_QUERY_KEY, playbookId ?? null],
    queryFn: async () => (await api.getEffectiveConductProfile(playbookId)).data,
    enabled: canRead,
  });
}

/**
 * Os playbooks ATIVOS que apontam para uma persona.
 *
 * A tela da Persona precisa disto porque uma persona pode ser apontada por VÁRIOS playbooks
 * (medido no dev: 3), então a cadeia do meio BIFURCA e não existe «um» efetivo para uma
 * persona que não opinou.
 */
export function usePersonaConductUsage(
  personaId: string | null,
): UseQueryResult<{ playbooks: { id: string; name: string }[] }> {
  const canRead = useCapability(CONDUCT_PROFILE_CAPABILITY);
  return useQuery({
    queryKey: [CONDUCT_PERSONA_USAGE_QUERY_KEY, personaId],
    queryFn: async () => (await api.getPersonaConductUsage(personaId!)).data,
    enabled: canRead && personaId !== null,
  });
}

/** Acha a resolução de um eixo; `null` enquanto a query não resolveu. */
export function axisResolution(
  resolution: ConductProfileResolution | undefined,
  axis: string,
): ConductProfileResolution['axes'][number] | null {
  if (resolution === undefined) return null;
  return resolution.axes.find((a) => a.field === axis) ?? null;
}
